/*
 * No `satisfies` in this file or in value-colors.ts: Angular's AOT evaluator
 * cannot resolve a value through it, and component `styles` interpolate
 * `${BdValueColor.negative}`, which then fails with "could not be determined
 * statically".
 */

/**
 * The one place where BinDoc's own theme colours are defined (BDA-6801).
 *
 * PrimeNG's semantic tokens cover text, content surfaces and form fields, but
 * not status colours outside a component nor every surface layer the
 * styleguide names. Features used to hand-write `light-dark()` pairs and
 * fixed primitives for these, one feature at a time, and they disagreed
 * (three error reds) or did not invert in dark mode (`--p-red-50` banners).
 *
 * Everything here feeds three consumers, so no value is typed twice:
 *
 * - the presets: {@link BD_SEMANTIC_TOKENS} is spread into `semantic.bd` of
 *   every preset and emitted at `:root` as `--p-bd-…` variables. Component
 *   styles and inline styles read those (`var(--p-bd-status-error-color)`);
 * - Tailwind: `src/styles/bd-tailwind-theme.css` names the same variables as
 *   utilities (`text-bd-error`, `bg-bd-error-tint`, `bg-bd-surface-card`);
 * - canvas renderers (Chart.js), which cannot resolve CSS variables:
 *   {@link bdStatusInk} returns the resolved hex for the active scheme.
 *
 * Styleguide: `docs/styleguide/colors/index.html` § Severity colours and
 * § Surface hierarchy.
 */

/** The four states a message, figure or badge can carry. */
export type BdStatus = 'success' | 'error' | 'warning' | 'info';

/** A colour for the light scheme and its counterpart for the dark scheme. */
export interface BdColorPair {
  readonly light: string;
  readonly dark: string;
}

/**
 * Status ink: the text and icon colour of a state. Each side reaches WCAG AA
 * (≥ 4.5 : 1) on its content surface and on the status tint, in both schemes.
 * Brand orange `#F58B25` is no text colour (2.4 : 1 on white); it stays a
 * non-text accent.
 */
export const BD_STATUS_INK: Readonly<Record<BdStatus, BdColorPair>> = {
  /**
   * A notch below Material green 800 (`#2E7D32` drops to 4.49 : 1 on its own
   * tint) / green 400.
   */
  success: { light: '#28772C', dark: '#66BB6A' },
  /** Material red 800 / red 300 — the same red as an invalid form field. */
  error: { light: '#C62828', dark: '#E57373' },
  /** No orange, amber or yellow primitive reaches AA on white: a darkened brand orange / orange 300. */
  warning: { light: '#A14F00', dark: '#FFB74D' },
  /** Brand purple / Material purple 200. */
  info: { light: '#452A71', dark: '#CE93D8' },
};

/** Share of the ink mixed into the content surface for a status background. */
export const BD_STATUS_TINT_PERCENT = 10;

/**
 * Surface layers without a PrimeNG semantic token. Page and panel already have
 * one: `--p-content-background` and `--p-content-hover-background`.
 */
export const BD_SURFACE_PAIRS: Readonly<
  Record<'subtle' | 'card', BdColorPair>
> = {
  /**
   * A quiet band one step off the page: table toolbars, info boxes, section
   * headers. Dark is 800, not 900 — 900 is the dark page itself, so the band
   * would disappear.
   */
  subtle: { light: '{surface.50}', dark: '{surface.800}' },
  /** A card that sits on a panel (`--p-content-hover-background`). */
  card: { light: '{surface.200}', dark: '{surface.700}' },
};

function lightDark(pair: BdColorPair): string {
  return `light-dark(${pair.light}, ${pair.dark})`;
}

function statusTokens(pair: BdColorPair): { color: string; tint: string } {
  const color = lightDark(pair);
  return {
    color,
    tint: `color-mix(in srgb, ${color} ${BD_STATUS_TINT_PERCENT}%, {content.background})`,
  };
}

/**
 * `semantic.bd` of every preset. PrimeUIX emits these outside the colour
 * scheme blocks, so they exist at `:root` in light-only apps as well; the
 * `light-dark()` values follow the document's `color-scheme`.
 */
export const BD_SEMANTIC_TOKENS = {
  status: {
    success: statusTokens(BD_STATUS_INK.success),
    error: statusTokens(BD_STATUS_INK.error),
    warning: statusTokens(BD_STATUS_INK.warning),
    info: statusTokens(BD_STATUS_INK.info),
  },
  surface: {
    subtle: lightDark(BD_SURFACE_PAIRS.subtle),
    card: lightDark(BD_SURFACE_PAIRS.card),
  },
} as const;

function statusPair(status: BdStatus): BdColorPair {
  switch (status) {
    case 'success':
      return BD_STATUS_INK.success;
    case 'error':
      return BD_STATUS_INK.error;
    case 'warning':
      return BD_STATUS_INK.warning;
    case 'info':
      return BD_STATUS_INK.info;
  }
}

/**
 * Resolved status ink for renderers that cannot read CSS variables (Chart.js
 * canvas). Pass the `BD_DARK_THEME` signal's value.
 */
export function bdStatusInk(status: BdStatus, dark: boolean): string {
  const pair = statusPair(status);
  return dark ? pair.dark : pair.light;
}

function messageSeverity(status: BdStatus): Record<string, unknown> {
  const color = `{bd.status.${status}.color}`;
  return {
    background: `{bd.status.${status}.tint}`,
    borderColor: `color-mix(in srgb, ${color} 30%, transparent)`,
    color,
    closeButton: {
      hoverBackground: `color-mix(in srgb, ${color} 12%, transparent)`,
      focusRing: { color },
    },
    outlined: { color, borderColor: color },
    simple: { color },
  };
}

/**
 * `p-message` reads the same status colours: the Material defaults (green 600,
 * red 600, yellow 900, blue 600) miss AA for text on white.
 */
export const BD_MESSAGE_TOKENS = {
  success: messageSeverity('success'),
  warn: messageSeverity('warning'),
  error: messageSeverity('error'),
  info: messageSeverity('info'),
};
